// Lucide-style inline SVG icons, ported from the design's icons.jsx.
// API: <I.Name size={n} /> — keeps the design JSX working as-is.

import type { CSSProperties, ReactNode } from "react";

interface IconProps {
  size?: number;
  style?: CSSProperties;
}

interface InnerProps extends IconProps {
  d?: string;
  paths?: ReactNode;
  viewBox?: string;
}

function Icon({ d, paths, size = 16, viewBox = "0 0 24 24", style }: InnerProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
    >
      {paths || (d ? <path d={d} /> : null)}
    </svg>
  );
}

export const I = {
  Home: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <path d="M9 22V12h6v10" />
        </>
      }
    />
  ),
  Book: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </>
      }
    />
  ),
  Branch: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <line x1="6" y1="3" x2="6" y2="15" />
          <circle cx="18" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <path d="M18 9a9 9 0 0 1-9 9" />
        </>
      }
    />
  ),
  GitCommit: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <circle cx="12" cy="12" r="3" />
          <line x1="3" y1="12" x2="9" y2="12" />
          <line x1="15" y1="12" x2="21" y2="12" />
        </>
      }
    />
  ),
  GitMerge: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <circle cx="18" cy="18" r="3" />
          <circle cx="6" cy="6" r="3" />
          <path d="M6 21V9a9 9 0 0 0 9 9" />
        </>
      }
    />
  ),
  GitPullRequest: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <circle cx="18" cy="18" r="3" />
          <circle cx="6" cy="6" r="3" />
          <path d="M13 6h3a2 2 0 0 1 2 2v7" />
          <line x1="6" y1="9" x2="6" y2="21" />
        </>
      }
    />
  ),
  Cloud: (p: IconProps) => (
    <Icon {...p} d="M17.5 19a4.5 4.5 0 1 0-1.4-8.78A7 7 0 1 0 5.13 19z" />
  ),
  Settings: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </>
      }
    />
  ),
  ChevronRight: (p: IconProps) => <Icon {...p} d="m9 18 6-6-6-6" />,
  ChevronLeft: (p: IconProps) => <Icon {...p} d="m15 18-6-6 6-6" />,
  ArrowRight: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </>
      }
    />
  ),
  Check: (p: IconProps) => <Icon {...p} d="M20 6 9 17l-5-5" />,
  X: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </>
      }
    />
  ),
  Play: (p: IconProps) => <Icon {...p} d="M5 3v18l15-9z" />,
  Plus: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <line x1="12" y1="5" x2="12" y2="19" />
          <line x1="5" y1="12" x2="19" y2="12" />
        </>
      }
    />
  ),
  Save: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
          <polyline points="17 21 17 13 7 13 7 21" />
          <polyline points="7 3 7 8 15 8" />
        </>
      }
    />
  ),
  File: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
        </>
      }
    />
  ),
  FileCode: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <path d="m10 13-2 2 2 2" />
          <path d="m14 17 2-2-2-2" />
        </>
      }
    />
  ),
  MessageCircle: (p: IconProps) => (
    <Icon
      {...p}
      d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
    />
  ),
  Bell: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </>
      }
    />
  ),
  Search: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </>
      }
    />
  ),
  Eye: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </>
      }
    />
  ),
  Lock: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </>
      }
    />
  ),
  Unlock: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 9.9-1" />
        </>
      }
    />
  ),
  Sparkles: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3z" />
      }
    />
  ),
  Trophy: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
          <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
          <path d="M4 22h16" />
          <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
          <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
          <path d="M18 2H6v7a6 6 0 0 0 12 0V2z" />
        </>
      }
    />
  ),
  Refresh: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <polyline points="23 4 23 10 17 10" />
          <polyline points="1 20 1 14 7 14" />
          <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
        </>
      }
    />
  ),
  Terminal: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <polyline points="4 17 10 11 4 5" />
          <line x1="12" y1="19" x2="20" y2="19" />
        </>
      }
    />
  ),
  HelpCircle: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <circle cx="12" cy="12" r="10" />
          <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </>
      }
    />
  ),
  Users: (p: IconProps) => (
    <Icon
      {...p}
      paths={
        <>
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </>
      }
    />
  ),
};

export type IconName = keyof typeof I;
